import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ApiService } from './api.service';
import { CookieConsentService } from './cookie-consent.service';

interface GtagWindow extends Window {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
}

function gaDisableKey(measurementId: string): string {
  return `ga-disable-${measurementId}`;
}

// The superadmin panel is an internal staff tool, not customer product
// usage — excluded from both pageviews and trackEvent() calls (see
// analytics.ts). /dashboard used to be excluded too (it's not the
// marketing funnel this service was originally built for), but product
// usage — onboarding, feature adoption, upgrade prompts — is exactly what
// informs decisions now, so it's tracked like any other route.
const EXCLUDED_PATH_PREFIXES = ['/admin'];

export function isGaTrackedPath(path: string): boolean {
  return !EXCLUDED_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

/**
 * Loads Google Analytics (GA4) for the marketing site, gated on the visitor's
 * cookie consent choice (GDPR — see CookieConsentService and
 * cookie-consent-banner.component.ts). The measurement ID comes from GET
 * /api/platform-config (same delivery as widgetUrl/monitoring.dsn — see
 * monitoring.ts); an unset GA_MEASUREMENT_ID means this stays fully dormant.
 *
 * Unlike Plausible/Sentry (fire-and-forget from main.ts), this needs Router
 * (to fire a page_view per SPA navigation, since Angular has no full page
 * loads) and to react to consent changing later via the banner/footer link —
 * so it's an injectable, driven from AppComponent instead.
 */
@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly api = inject(ApiService);
  private readonly consent = inject(CookieConsentService);
  private readonly router = inject(Router);

  private measurementId: string | null = null;
  private scriptLoaded = false;
  private lastTrackedPath: string | null = null;

  /** Called once from AppComponent's constructor; no-ops during prerendering. */
  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.api.getPlatformConfig().subscribe({
      next: (config) => {
        const id = config.analytics?.googleAnalyticsId;
        if (!id) return;
        this.measurementId = id;

        // status$ (a BehaviorSubject) replays the current choice immediately
        // on subscribe, then every later change (banner accept/reject,
        // footer "Cookie preferences" reset+re-choose) — synchronously, no
        // change-detection/microtask scheduling involved.
        this.consent.status$.subscribe((status) => {
          if (status === 'granted') {
            this.enable();
          } else {
            this.disable();
          }
        });
      },
      error: (err) => console.error('Failed to load platform config for analytics:', err),
    });
  }

  private enable(): void {
    if (!this.measurementId) return;
    this.setDisabled(false);

    if (this.scriptLoaded) {
      this.trackCurrentPage();
      return;
    }
    this.scriptLoaded = true;

    const w = window as GtagWindow;
    w.dataLayer = w.dataLayer || [];
    const gtag = (...args: unknown[]) => {
      w.dataLayer!.push(args);
    };
    w.gtag = w.gtag || gtag;

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    document.head.appendChild(script);

    w.gtag('js', new Date());
    // Google Consent Mode v2 signal — required in addition to our own
    // ga-disable-* gating above. Without this, Google's tag silently drops
    // hits for EEA/UK/Switzerland visitors regardless of our own consent
    // banner state, because it has no way to know the visitor opted in
    // (our banner is a custom mechanism Google's tag doesn't observe). This
    // only runs from enable(), which only fires once the visitor has
    // already granted consent via CookieConsentService, so analytics_storage
    // can go straight to 'granted'. We don't use ad_storage/ad_user_data/
    // ad_personalization (no ads or remarketing here) so those stay denied.
    w.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'granted',
    });
    // send_page_view: false — this is an SPA; we fire page_view manually per
    // Angular route change below instead of once at initial load, and skip
    // dashboard/admin routes entirely (see isGaTrackedPath).
    w.gtag('config', this.measurementId, { send_page_view: false });

    this.trackCurrentPage();
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.trackCurrentPage());
  }

  private disable(): void {
    this.setDisabled(true);
  }

  // Google's documented client-side opt-out flag: once set, gtag.js (even
  // already loaded) drops every hit for this measurement ID. Used both for
  // an initial "denied" choice and for withdrawing a prior "granted" one.
  private setDisabled(disabled: boolean): void {
    if (!this.measurementId) return;
    (window as unknown as Record<string, unknown>)[gaDisableKey(this.measurementId)] = disabled;
  }

  private trackCurrentPage(): void {
    const path = this.router.url.split('?')[0].split('#')[0];
    if (!isGaTrackedPath(path)) return;
    // NavigationEnd re-fires for the initial route on hydration, right after
    // the direct call below already tracked it — this dedupes that specific
    // double-count without blocking a real later revisit to the same path
    // (any other navigation in between clears the guard).
    if (path === this.lastTrackedPath) return;
    this.lastTrackedPath = path;

    const w = window as GtagWindow;
    w.gtag?.('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }
}
